import React, { useEffect, useState } from "react";
import api, { formatCurrency } from "@/lib/api";
import { motion } from "framer-motion";
import { ShieldAlert, Users, TrendingUp, BarChart3, CheckCircle2, X, Edit3, Loader2, Eye } from "lucide-react";
import { toast } from "sonner";
import TxDetailModal from "@/components/admin/TxDetailModal";

const TABS = ["Transactions", "Rates", "Users", "Fraud", "Analytics"];

export default function Admin() {
  const [tab, setTab] = useState("Transactions");

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-10" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#00C853] font-semibold">Admin Console</p>
            <h1 className="font-display text-3xl font-bold text-[#0B3C5D] mt-1 tracking-tight">Operations Dashboard</h1>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`text-sm px-4 py-2 rounded-full border ${tab === t ? "bg-[#0B3C5D] text-white border-[#0B3C5D]" : "bg-white border-slate-200 text-slate-700 hover:border-[#0B3C5D]"}`}
              data-testid={`admin-tab-${t.toLowerCase()}`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "Transactions" && <AdminTransactions />}
        {tab === "Rates" && <AdminRates />}
        {tab === "Users" && <AdminUsers />}
        {tab === "Fraud" && <AdminFraud />}
        {tab === "Analytics" && <AdminAnalytics />}
      </div>
    </div>
  );
}

function AdminTransactions() {
  const [txs, setTxs] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [openTx, setOpenTx] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get(`/admin/transactions${filter ? `?status=${filter}` : ""}`);
    setTxs(data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const update = async (tx_id, status) => {
    try {
      await api.patch(`/admin/transactions/${tx_id}`, { status });
      toast.success(`Updated ${tx_id} -> ${status}`);
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-200 p-6">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {["", "pending", "verifying", "approved", "paid", "rejected"].map((s) => (
          <button key={s || "all"} onClick={() => setFilter(s)}
            className={`text-xs px-3 py-1.5 rounded-full border ${filter === s ? "border-[#0B3C5D] bg-[#0B3C5D] text-white" : "border-slate-200 text-slate-600"}`}
            data-testid={`admin-tx-filter-${s || "all"}`}
          >
            {s || "All"}
          </button>
        ))}
      </div>

      {loading ? <Loader2 className="w-5 h-5 animate-spin text-[#0B3C5D]" /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr className="text-left">
                <th className="px-4 py-3 font-medium">TX ID</th>
                <th className="px-4 py-3 font-medium">User</th>
                <th className="px-4 py-3 font-medium">Brand</th>
                <th className="px-4 py-3 font-medium">Amount</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Risk</th>
                <th className="px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {txs.length === 0 && <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-500">No transactions yet.</td></tr>}
              {txs.map((t) => (
                <tr key={t.tx_id} className="border-t border-slate-100 hover:bg-slate-50/60">
                  <td className="px-4 py-3 font-mono text-xs text-[#0B3C5D]">{t.tx_id}</td>
                  <td className="px-4 py-3 text-slate-700 text-xs">{t.user_email}</td>
                  <td className="px-4 py-3 font-medium">{t.brand_name}</td>
                  <td className="px-4 py-3">{formatCurrency(t.amount, t.currency)}</td>
                  <td className="px-4 py-3 capitalize text-xs">{t.status}</td>
                  <td className="px-4 py-3 text-xs">{t.risk_flags?.length ? <span className="text-amber-600">{t.risk_flags.join(",")}</span> : "—"}</td>
                  <td className="px-4 py-3 flex flex-wrap gap-1">
                    <button onClick={() => setOpenTx(t.tx_id)} className="text-xs px-2 py-1 rounded bg-slate-100 text-[#0B3C5D] border border-slate-200 flex items-center gap-1" data-testid={`admin-view-${t.tx_id}`}><Eye className="w-3 h-3" /> View</button>
                    <button onClick={() => update(t.tx_id, "approved")} className="text-xs px-2 py-1 rounded bg-cyan-50 text-cyan-700 border border-cyan-200" data-testid={`admin-approve-${t.tx_id}`}>Approve</button>
                    <button onClick={() => update(t.tx_id, "paid")} className="text-xs px-2 py-1 rounded bg-[#00C853]/10 text-[#00a543] border border-[#00C853]/30" data-testid={`admin-paid-${t.tx_id}`}><CheckCircle2 className="w-3 h-3 inline"/> Paid</button>
                    <button onClick={() => update(t.tx_id, "rejected")} className="text-xs px-2 py-1 rounded bg-red-50 text-red-700 border border-red-200" data-testid={`admin-reject-${t.tx_id}`}><X className="w-3 h-3 inline"/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {openTx && (
        <TxDetailModal txId={openTx} onClose={() => setOpenTx(null)} onUpdated={load} />
      )}
    </div>
  );
}

function AdminRates() {
  const [data, setData] = useState({ brands: [], countries: [], overrides: [] });
  const [edit, setEdit] = useState(null);
  const [val, setVal] = useState("");

  const load = async () => setData((await api.get("/admin/rates")).data);
  useEffect(() => { load(); }, []);

  const overrideMap = {};
  data.overrides.forEach((o) => { overrideMap[`${o.brand_id}_${o.country}`] = o.rate_pct; });

  const save = async () => {
    const [brand_id, country] = edit.split("|");
    try {
      await api.put("/admin/rates", { brand_id, country, rate_pct: Number(val) });
      toast.success("Rate updated");
      setEdit(null); setVal("");
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-200 p-6">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr><th className="px-4 py-3 text-left font-medium">Brand</th>
              {data.countries.map((c) => <th key={c.code} className="px-4 py-3 font-medium text-center">{c.code}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.brands.map((b) => (
              <tr key={b.id} className="border-t border-slate-100">
                <td className="px-4 py-3 font-medium">{b.name}</td>
                {data.countries.map((c) => {
                  const key = `${b.id}|${c.code}`;
                  const overridden = overrideMap[`${b.id}_${c.code}`];
                  const rate = overridden ?? b.rate[c.code];
                  return (
                    <td key={c.code} className="px-4 py-3 text-center">
                      {edit === key ? (
                        <div className="flex items-center gap-1">
                          <input value={val} onChange={(e) => setVal(e.target.value)} type="number" step="0.01" min="0" max="1" className="w-16 border border-slate-200 rounded px-2 py-1 text-xs" />
                          <button onClick={save} className="text-xs text-[#00C853] font-semibold" data-testid={`admin-rate-save-${b.id}-${c.code}`}>Save</button>
                          <button onClick={() => setEdit(null)} className="text-xs text-slate-400">×</button>
                        </div>
                      ) : (
                        <button onClick={() => { setEdit(key); setVal(rate); }} className="text-xs font-semibold text-[#0B3C5D] hover:text-[#00C853]" data-testid={`admin-rate-cell-${b.id}-${c.code}`}>
                          {(rate * 100).toFixed(1)}% {overridden && <Edit3 className="w-2.5 h-2.5 inline ml-1" />}
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminUsers() {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/admin/users").then((r) => setUsers(r.data)); }, []);

  const ban = async (id, banned) => {
    await api.patch(`/admin/users/${id}/ban?banned=${banned}`);
    setUsers((u) => u.map((x) => x.id === id ? { ...x, banned } : x));
    toast.success(banned ? "User banned" : "User unbanned");
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-200 p-6">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr className="text-left">
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Phone</th>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Country</th>
              <th className="px-4 py-3 font-medium">Role</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-slate-100">
                <td className="px-4 py-3 text-xs" data-testid={`admin-user-email-${u.id}`}>{u.email}</td>
                <td className="px-4 py-3 text-xs text-slate-700" data-testid={`admin-user-phone-${u.id}`}>{u.phone || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3">{u.name}</td>
                <td className="px-4 py-3">{u.country}</td>
                <td className="px-4 py-3 capitalize">{u.role}</td>
                <td className="px-4 py-3">{u.banned ? <span className="text-red-600 text-xs font-semibold">Banned</span> : <span className="text-[#00a543] text-xs">Active</span>}</td>
                <td className="px-4 py-3">
                  {u.role !== "admin" && (
                    <button onClick={() => ban(u.id, !u.banned)} className={`text-xs px-2 py-1 rounded border ${u.banned ? "border-[#00C853] text-[#00a543]" : "border-red-200 text-red-700"}`} data-testid={`admin-ban-${u.id}`}>
                      {u.banned ? "Unban" : "Ban"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminFraud() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/fraud-alerts").then((r) => setItems(r.data)); }, []);
  return (
    <div className="bg-white rounded-3xl border border-slate-200 p-6">
      <div className="flex items-center gap-2 mb-4">
        <ShieldAlert className="w-5 h-5 text-amber-500" />
        <h2 className="font-display text-lg font-bold text-[#0B3C5D]">Fraud Alerts</h2>
      </div>
      {items.length === 0 ? <p className="text-sm text-slate-500">No flagged transactions.</p> : (
        <div className="space-y-3">
          {items.map((t) => (
            <div key={t.tx_id} className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
              <div className="flex items-center justify-between">
                <p className="font-mono text-xs text-[#0B3C5D]">{t.tx_id}</p>
                <p className="text-xs text-amber-700 font-semibold">{(t.risk_flags || []).join(", ")}</p>
              </div>
              <p className="mt-1 text-sm">{t.brand_name} — {formatCurrency(t.amount, t.currency)} — {t.user_email}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminAnalytics() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setA(r.data)); }, []);
  if (!a) return <Loader2 className="w-5 h-5 animate-spin" />;

  const Stat = ({ Icon, label, value }) => (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="w-9 h-9 rounded-lg bg-[#00C853]/10 flex items-center justify-center"><Icon className="w-4 h-4 text-[#00C853]" /></div>
      <p className="text-xs uppercase tracking-wider text-slate-500 mt-3">{label}</p>
      <p className="font-display text-2xl font-bold text-[#0B3C5D] mt-1">{value}</p>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat Icon={BarChart3} label="Total Transactions" value={a.total_tx} />
        <Stat Icon={TrendingUp} label="Total Paid" value={formatCurrency(a.total_paid)} />
        <Stat Icon={Users} label="Users" value={a.users} />
        <Stat Icon={ShieldAlert} label="Statuses" value={a.by_status.length} />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-3xl border border-slate-200 p-6">
          <h3 className="font-display font-bold text-[#0B3C5D] mb-4">Top brands</h3>
          {a.by_brand.length === 0 ? <p className="text-sm text-slate-500">No data yet.</p> :
            a.by_brand.map((b) => (
              <div key={b._id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <span className="text-sm">{b._id}</span>
                <span className="font-semibold text-sm">{b.count} sales</span>
              </div>
            ))}
        </div>
        <div className="bg-white rounded-3xl border border-slate-200 p-6">
          <h3 className="font-display font-bold text-[#0B3C5D] mb-4">By country</h3>
          {a.by_country.length === 0 ? <p className="text-sm text-slate-500">No data yet.</p> :
            a.by_country.map((c) => (
              <div key={c._id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <span className="text-sm">{c._id}</span>
                <span className="font-semibold text-sm">{c.count} sales</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
